import React, { useEffect } from 'react'
import {
  withStyles,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core'
import { data } from './components'
import { Icon } from '@iconify/react'
import styles from './styles'
import { useDispatch, useSelector } from 'react-redux'
import {
  editSectionOrderAction,
  setEndingCard,
} from 'src/reducers/course/action'
import { SectionTypes } from 'src/model/card'
import clsx from 'clsx'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { ButtonActionType, ButtonData, ButtonType } from 'src/model/button'
import { useCoursePublish } from 'src/contexts/course-publish-context'
import CompletionPublishState from '../completion-publish-state'
const LastPageHeader = props => {
  const { classes, sections, setSections, cardData, isEndingCardExist } = props
  const course = useSelector(state => state.course.course)
  const [open, setOpen] = React.useState(isEndingCardExist ? false : true)
  const [isLoading, setIsLoading] = React.useState(false)
  const onClickChange = () => {
    setOpen(!open)
  }
  useEffect(() => {
    if (isEndingCardExist) {
      setOpen(false)
    }
  }, [isEndingCardExist])

  const dummyData = data.find(x => x.type === cardData?.type)

  const cardType = dummyData?.title?.replace('<br/>', '')
  const icon = dummyData?.icon

  const dispatch = useDispatch()
  const publishState = useCoursePublish()

  // On a live course the switch is an edit like any other: learners keep reading the completion page
  // they already have until it is published, so say so rather than warning about a loss that also
  // takes their page off the air.
  const isLive = Boolean(publishState?.isPublished)
  const replaceWarning = isLive
    ? 'Changing completion type replaces the current settings and texts of this page. Learners keep seeing the current completion page until you publish the change.'
    : 'Changing completion type will delete your current settings and texts. Continue anyway?'

  const continueEnding = type => {
    if (!isEndingCardExist) {
      handleAddEndingCard(type)
      return
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 18px; font-weight: 600; line-height: 28px;">
                <Typography>Replace Current Settings?</Typography>
              </div>`,
          type: MessageBoxType.WARNING,
          message: replaceWarning,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => {
                dispatch(messageBoxHideAction())
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Continue',
              ButtonActionType.YES,
              () => {
                handleAddEndingCard(type)
                dispatch(messageBoxHideAction())
              },
            ),
          ],
          component: (
            <div
              style={{
                gap: '8px',
                color: 'var(--mcg-dark, #002E47)',
                fontFamily: 'Inter',
                fontSize: '14px ',
                fontWeight: 400,
                lineHeight: '22px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <p>{replaceWarning}</p>
            </div>
          ),
          maxWidth: '500px',
        }),
      ),
    )
  }

  const handleAddEndingCard = type => {
    setIsLoading(true)

    const newSection = {
      id: null,
      title: 'Ending',
      type: SectionTypes.ENDING,
      cards: [
        {
          id: null,
          type: type,
          title: 'Ending Page',
          orderOfCard: 10001,
        },
      ],
    }

    dispatch(
      editSectionOrderAction(
        course.id,
        { sections: [...sections, newSection] },
        response => {
          // The completion page comes back last whether it was created or pointed at a new type —
          // its id only changes in the first case, since a live page is repointed rather than
          // replaced.
          const endingSection = response.sections[response.sections.length - 1]

          dispatch(
            setEndingCard({
              ...newSection.cards[0],
              id: endingSection.cards[0].id,
            }),
          )

          // Publish state is derived server-side and this is the one edit that reaches it without
          // touching a card patch or the editor's autosave. Without this refetch the switch stayed
          // invisible to "Publish changes" until the page was reloaded — issue #562.
          if (publishState) {
            publishState.refresh()
          }

          setOpen(false)

          // The ending section is edited here, not in the outline.
          setSections([...sections].filter(x => x.type !== SectionTypes.ENDING))
          setIsLoading(false)
        },
      ),
    )
  }

  return (
    <div className={classes.root}>
      <div className={`${classes.container} ${open ? 'open' : ''}`}>
        <div className={classes.flexContainer}>
          <div className={classes.headerWrapper}>
            <Icon
              icon={icon ? icon : 'mynaui:flag-solid'}
              className={classes.icons}
              fontSize={16}
            />
            <Typography className={classes.pageTitle}>
              {cardType ? cardType : 'Customize Completion'}
            </Typography>
            {cardData?.id && <CompletionPublishState cardId={cardData.id} />}
          </div>
          <div
            style={{
              display: isEndingCardExist ? 'flex' : 'none',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <Button
              className={classes.changeCompletionButton}
              onClick={onClickChange}
            >
              {!open && (
                <Icon
                  icon={'qlementine-icons:swap-16'}
                  className={classes.icons}
                />
              )}
              <Typography className={classes.buttonText}>
                {!open ? 'Change completion' : ''}
              </Typography>
            </Button>
          </div>
          <div
            className={classes.helpContainer}
            onClick={() => {
              window.open(
                'https://intercom.help/mini-course-generator/en/articles/7864240-customizing-the-end-completion',
                '_blank',
              )
            }}
          >
            <Icon
              icon="solar:question-circle-outline"
              fontSize={16}
              color="#002E47"
              className={classes.helpIcon}
            />
            <Typography className={classes.helpText}>How it works?</Typography>
          </div>
        </div>
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <div className={`${classes.itemWrapper} ${open ? 'open' : ''}`}>
            {data.map((item, index) => (
              <div
                key={index}
                className={clsx({
                  [classes.itemContainer]: true,
                  [classes.itemSelected]: item.type === cardData?.type,
                })}
                onClick={() => continueEnding(item.type)}
              >
                <Icon icon={item.icon} className={classes.icon} />
                <Typography
                  className={classes.text}
                  dangerouslySetInnerHTML={{ __html: item.title }}
                ></Typography>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default withStyles(styles)(LastPageHeader)
